import { useState, useEffect } from 'react';
import { Tabs } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const DEFAULT_LANGUAGES = [
  { code: 'en', name: 'English', native_name: 'English' },
  { code: 'de', name: 'German', native_name: 'Deutsch' },
];

/** Derived convenience export for consumers that only need code strings */
export const DEFAULT_LANG_CODES = DEFAULT_LANGUAGES.map(l => l.code);

let cachedLanguages = null;

/**
 * LanguageTabs renders a Mantine <Tabs> with one tab per active language.
 * Uses the render-prop pattern: children receives { language, langCode }.
 *
 * Usage:
 * <LanguageTabs form={form} fields={['name', 'description']}>
 *   {({ language, langCode }) => (
 *     <TextInput label={`Name (${language.native_name})`} {...form.getInputProps(`name_${langCode}`)} />
 *   )}
 * </LanguageTabs>
 */
export default function LanguageTabs({ children, value, onChange, form }) {
  const [languages, setLanguages] = useState(cachedLanguages || []);
  const [activeTab, setActiveTab] = useState(value || (cachedLanguages?.[0]?.code) || null);

  useEffect(() => {
    if (cachedLanguages) return;
    fetch(`${API_BASE_URL}/languages`)
      .then(r => r.json())
      .then(data => {
        cachedLanguages = data;
        setLanguages(data);
        if (!activeTab && data.length > 0) setActiveTab(data[0].code);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (value !== undefined && value !== activeTab) setActiveTab(value);
  }, [value]);

  if (languages.length === 0) {
    return typeof children === 'function'
      ? children({ language: DEFAULT_LANGUAGES[0], langCode: DEFAULT_LANGUAGES[0].code })
      : children;
  }

  const handleChange = (tab) => {
    setActiveTab(tab);
    if (onChange) onChange(tab);
  };

  return (
    <Tabs value={activeTab} onChange={handleChange}>
      <Tabs.List>
        {languages.map(lang => (
          <Tabs.Tab key={lang.code} value={lang.code} leftSection={<IconLanguage size={16} />}>
            {lang.native_name}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {languages.map(lang => (
        <Tabs.Panel key={lang.code} value={lang.code} pt="md">
          {typeof children === 'function'
            ? children({ language: lang, langCode: lang.code })
            : children}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

/** Hook to fetch and cache active languages. */
export function useLanguages() {
  const [languages, setLanguages] = useState(cachedLanguages || []);

  useEffect(() => {
    if (cachedLanguages) {
      if (languages.length === 0) setLanguages(cachedLanguages);
      return;
    }
    fetch(`${API_BASE_URL}/languages`)
      .then(r => r.json())
      .then(data => {
        cachedLanguages = data;
        setLanguages(data);
      })
      .catch(() => {});
  }, []);

  return languages;
}
